/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  formatEndDate,
  formattedDate,
  isObjectOrArrayAndEmpty,
  returnFormattedDate
} from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  IEnergyOptimizationComponentProps,
  IEnergyTableOptimizationWidgetProps
} from '../EnergyOptimizationInterfaces';
import useMounted from '../../../shared/useMount/useMount';
import EnergyOptimizationWidget from './EnergyOptimizationWidget';
import {
  IAny,
  IEmaasEnergyOptimizationComponentData,
  ISiteCard
} from '../../IEnergyManagementProps';
import EnergyOptimizationTableWidget from './EnergyOptimizationTableWidget';
import { IMessageDescriptor } from '@scuf/localization/dist/types';
import { ApiContext } from '../../../../context/ApiContext';
import moment from 'moment';
import { differenceInDays, isThisMonth, subDays } from 'date-fns';

interface IEmaasEnergyOptimizationHOCContent extends ISiteCard {
  data: IEmaasEnergyOptimizationComponentData;
  mainGraph?: boolean;
  isAllUtility?: boolean;
  startTime?: IAny;
  endTime?: IAny;
  toggleEnableEoTable?: boolean;
}

const EnergyOptimizationComponentWithContent = (
  props: IEmaasEnergyOptimizationHOCContent
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    data,
    onRefresh,
    mainGraph,
    isAllUtility,
    startTime,
    endTime,
    toggleEnableEoTable
  } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <>
      <EnergyOptimizationWidget
        isAllUtility={isAllUtility}
        data={data}
        mainGraph={mainGraph}
      />
    </>
  );
};

const EnergyOptimizationComponentUtilityWithTableContent = (
  props: IEnergyTableOptimizationWidgetProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  if (isObjectOrArrayAndEmpty(props.data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
        />
      </div>
    );
  }

  return (
    <>
      <EnergyOptimizationTableWidget {...props} />
    </>
  );
};

export const EnergyOptimizationComponentWithLoader = withLoadingStatus(
  withErrorStatus(EnergyOptimizationComponentWithContent)
);

export const EnergyOptimizationUtilityComponentTableWithLoader = withLoadingStatus(
  withErrorStatus(EnergyOptimizationComponentUtilityWithTableContent)
);

const EnergyOptimizationComponent = (
  props: IEnergyOptimizationComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  // const { bearerToken, requestContext, saveDataInSite, isAllUtility } = props;

  const apiContext = useContext(ApiContext);

  const {
    bearerToken,
    requestContext,
    saveDataInSite,
    isAllUtility,
    setEoOperationData
  } = props;
  const { toggleEnableEoTable } = apiContext;
  let startTime = '';
  if (requestContext.context.startTime) {
    startTime = formattedDate(requestContext.context.startTime);
  }
  let endTime = '';
  if (requestContext.context.endTime) {
    endTime = formattedDate(requestContext.context.endTime);
  }

  let connectorConfig: IAny = {
    name: 'emaas-energy-optimization',
    options: {
      startDate: startTime,
      endDate: endTime
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      dataConnector && dataConnector.actions && dataConnector.actions.getAll();
    }
  }, [requestContext]);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const energyOptimization = (all as unknown) as IEmaasEnergyOptimizationComponentData;
  let isDataAvailable = false;
  if (energyOptimization) {
    isDataAvailable = true;
  }
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [StatusCode, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as any);

  const getEOPlannedOFFTime = (data: any) => {
    const eoRuntime = data?.dataItems?.filter(
      (item: { name: string }) => item.name === 'EOStatusRuntime'
    )[0];
    if (eoRuntime && eoRuntime.values && eoRuntime.values.length > 0) {
      if (eoRuntime.values[0].value === 0) {
        return '--';
      }
      return eoRuntime.values?.reduce((total: any, obj: any) => {
        if (obj && obj.properties && !obj.properties[3]) {
          return total;
        } else if (obj && obj.properties && obj.properties[3]) {
          return total + Number(obj.properties[3]);
        }
        return total;
      }, 0);
    }
  };

  const getTotalActiveHours = (data: IAny) => {
    const eoRuntime = data?.dataItems?.filter(
      (item: { name: string }) => item.name === 'EOStatusRuntime'
    )[0];
    if (eoRuntime && eoRuntime.values && eoRuntime.values.length > 0) {
      return eoRuntime.values?.reduce((total: any, obj: any) => {
        if (obj && obj.properties && !obj.properties[1]) {
          return total;
        } else if (obj && obj.properties && obj.properties[1]) {
          return total + Number(obj.properties[1]);
        }
        return total;
      }, 0);
    }
  };

  const checkIfThisMonthIsSelected = (endTime: string) => {
    const isCurrentMonth = isThisMonth(new Date(endTime.split('T')[0]));
    const end = moment(new Date(endTime)).format('YYYY-MM-DD');
    if (isCurrentMonth) {
      if (end >= moment(new Date()).format('YYYY-MM-DD')) {
        return subDays(new Date(), 1).toISOString();
      }
    }
    return endTime;
  };

  const getEOBaselineTime = (data: any) => {
    const eoRuntime = data?.dataItems?.filter(
      (item: { name: string }) => item.name === 'EOStatusRuntime'
    )[0];
    const end = checkIfThisMonthIsSelected(endTime);
    var hours = (differenceInDays(new Date(end), new Date(startTime)) + 1) * 24;
    const totalPlannedOff = getEOPlannedOFFTime(data);

    if (!isNaN(totalPlannedOff)) {
      const percentageBaselineTime = (totalPlannedOff / hours) * 100;
      const totalActiveHours = getTotalActiveHours(data);
      let totalDownTime = 0;
      if (!isNaN(totalActiveHours)) {
        totalDownTime = hours - (totalActiveHours + totalPlannedOff);
        if (data && data.summary) {
          if (totalActiveHours !== 0) {
            data.summary.opportunityFromEO =
              (data.summary.estimatedSavings / totalActiveHours) *
              totalDownTime;
          } else {
            data.summary.opportunityFromEO = null;
          }
        }
      }
      if (data && data.summary && !isNaN(percentageBaselineTime)) {
        data.summary.timeUsedForBaseline = percentageBaselineTime;
      }
    } else {
      if (data && data.summary) {
        data.summary.opportunityFromEO = null;
        data.summary.timeUsedForBaseline = null;
      }
    }
    if (
      eoRuntime?.values?.length &&
      eoRuntime.values[0].properties &&
      Object.keys(eoRuntime.values[0].properties).length === 0
    ) {
      data.summary.opportunityFromEO = null;
    }
  };

  const calculateActiveHoursForPastDays = (data: IAny) => {
    const secondsOfDay = 86400;
    let optimizationDayType = data?.filter(
      (dt: { [key: string]: string }) => dt.name === 'DayType'
    )[0];
    let enabledDates = optimizationDayType?.values
      ?.filter((x: { [key: string]: string | number }) => {
        if (x.value === 0 || x.value === 2) {
          return x;
        }
      })
      ?.map((y: { [key: string]: string }) => y.dataTimeStamp);

    let activeDurations = data?.filter(
      (dt: { [key: string]: string }) => dt.name === 'ActiveDuration'
    )[0];

    if (activeDurations === null) {
      return;
    }

    return activeDurations?.values?.filter((x: { [key: string]: string }) =>
      enabledDates?.includes(x.dataTimeStamp)
    );
  };

  const includeActiveHoursInData = (
    eoStatusRuntime: IAny,
    activeHours: IAny
  ) => {
    eoStatusRuntime?.values?.forEach((eoStatus: IAny) => {
      if (
        eoStatus &&
        eoStatus.properties &&
        Object.keys(eoStatus.properties).length === 0
      ) {
        // if(activeHours && activeHours.length)
        let ind = activeHours?.findIndex(
          (activeHour: { [key: string]: string }) =>
            activeHour.dataTimeStamp.split('T')[0] ===
            eoStatus.dataTimeStamp.split('T')[0]
        );
        if (ind !== -1) {
          const hours = (activeHours[ind].value * 24) / 86400;
          eoStatus.properties['1'] = hours.toString();
        }
        return false;
      } else {
        return true;
        // do nothing
      }
    });
  };

  const computeTimeUsedForBaselining = (data: IAny) => {
    const eoRuntime = data?.dataItems?.filter(
      (item: { name: string }) => item.name === 'EOStatusRuntime'
    )[0];
    includeActiveHoursInData(
      eoRuntime,
      calculateActiveHoursForPastDays(data?.dataItems)
    );
    getEOBaselineTime(data);
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(energyOptimization);
        setEoOperationData && setEoOperationData(energyOptimization);
        computeTimeUsedForBaselining(energyOptimization);
        saveDataInSite && saveDataInSite(energyOptimization);
        apiContext.saveEOStatusRuntime &&
          apiContext.saveEOStatusRuntime(
            energyOptimization?.dataItems?.filter(
              (item) => item.name === 'EOStatusRuntime'
            )[0],
            energyOptimization?.dataItems?.filter(
              (item) => item.name === 'DayType'
            )[0]
          );

        apiContext.saveEoOperationalTime &&
          apiContext.saveEoOperationalTime(
            energyOptimization?.summary?.operationalTimePercentage
          );
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <EnergyOptimizationComponentWithLoader
        loading={isLoading || !apiContext.isStatusCompleted}
        error={errorInfo}
        statusCode={StatusCode}
        onRefresh={onRefresh}
        data={data}
        loadingText={t(d.LOADING)}
        title={t(d.CONNECTIONERRORTITLE)}
        noDataMessage={t(d.NODATAAVAILABLE)}
        connectionErrorMessage={t(d.CONNECTIONERROR)}
        mainGraph={props.mainGraph}
        isAllUtility={isAllUtility}
        startTime={startTime}
        endTime={endTime}
        toggleEnableEoTable={toggleEnableEoTable}
      />
      {toggleEnableEoTable && !props.mainGraph && (
        <div className="EOTableData">
          <EnergyOptimizationUtilityComponentTableWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={StatusCode}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            isAllUtility={isAllUtility}
            data={data}
            startTime={startTime}
            endTime={checkIfThisMonthIsSelected(endTime)}
            isLoading={!isLoading}
          />
        </div>
      )}
    </>
  );
};
export default EnergyOptimizationComponent;
